import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { CopyButton } from '@reflex/ui/components/shared/CopyButton';
import { cn } from '@reflex/ui/lib/utils';
import {
  highlightCode,
  type HighlightedLines,
  type ThemedToken,
} from '@reflex/ui/lib/syntax-highlight';

interface CodeBlockProps {
  children: string;
  className?: string;
  copyable?: boolean;
  /** Optional filename or title shown above the code content. */
  title?: string;
  /**
   * Language hint for syntax highlighting. When omitted, the language is
   * auto-detected from `title` (treated as a filename). Pass `null` to
   * explicitly disable highlighting.
   */
  language?: string | null;
  /** Extra action nodes rendered in the header bar next to the copy button. */
  headerActions?: React.ReactNode;
  /** Max height before scrolling. Defaults to 400px. Pass "none" to disable. */
  maxHeight?: string | 'none';
  /** Compact density for inline/tool-call contexts. */
  compact?: boolean;
}

export function CodeBlock({
  children,
  className,
  copyable = true,
  title,
  language,
  headerActions,
  maxHeight = '400px',
  compact = false,
}: CodeBlockProps) {
  const { resolvedTheme } = useTheme();
  const highlightTheme = resolvedTheme === 'light' ? ('light' as const) : ('dark' as const);
  const shouldHighlight = language !== null && !!children;
  const [tokens, setTokens] = useState<HighlightedLines | null>(null);

  useEffect(() => {
    if (!shouldHighlight) return;
    let cancelled = false;
    void highlightCode(children, title ?? '', highlightTheme, language).then((result) => {
      if (!cancelled) setTokens(result);
    });
    return () => {
      cancelled = true;
    };
  }, [children, title, language, highlightTheme, shouldHighlight]);

  return (
    <div className={cn('relative rounded-md border border-border/40 bg-muted/30', className)}>
      {(title || copyable) && (
        <div
          className={cn(
            'flex items-center justify-between border-b border-border/30',
            compact ? 'px-2.5 py-1' : 'px-3 py-1.5',
          )}
        >
          {title ? (
            <span
              className={cn(
                'text-muted-foreground',
                compact ? 'font-mono text-3xs' : 'text-2xs font-medium',
              )}
            >
              {title}
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1">
            {headerActions}
            {copyable && (
              <CopyButton
                text={children}
                className={cn(
                  'text-muted-foreground transition-colors hover:text-foreground',
                  compact ? 'h-4 w-4' : 'h-5 w-5',
                )}
              />
            )}
          </div>
        </div>
      )}
      <div
        className={cn(
          'overflow-auto font-mono leading-relaxed whitespace-pre-wrap',
          compact ? 'px-2.5 py-2 text-2xs text-foreground/80' : 'p-3 text-xs',
        )}
        style={maxHeight !== 'none' ? { maxHeight } : undefined}
      >
        {shouldHighlight && tokens ? <HighlightedCode tokens={tokens} /> : children}
      </div>
    </div>
  );
}

function HighlightedCode({ tokens }: { tokens: HighlightedLines }) {
  return (
    <>
      {tokens.map((line, i) => (
        <span key={i}>
          {line.map((token: ThemedToken, j: number) => (
            <span key={j} style={{ color: token.color }}>
              {token.content}
            </span>
          ))}
          {i < tokens.length - 1 && '\n'}
        </span>
      ))}
    </>
  );
}
